<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
    <form @submit.prevent="handleSubmit">
      <!-- 标题输入 -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          公告标题 <span class="text-red-500">*</span>
        </label>
        <input v-model="formData.title" type="text" placeholder="请输入公告标题"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          :class="{ 'border-red-500': errors.title }" />
        <p v-if="errors.title" class="mt-1 text-sm text-red-500">{{ errors.title }}</p>
      </div>

      <!-- 状态选择 -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          状态 <span class="text-red-500">*</span>
        </label>
        <select v-model="formData.status"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
          <option :value="AnnouncementStatus.DRAFT">草稿</option>
          <option :value="AnnouncementStatus.PUBLISHED">已发布</option>
        </select>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          草稿状态的公告不会在用户端显示,只有已发布的公告才会对用户可见
        </p>
      </div>

      <!-- 优先级选择 -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          优先级 <span class="text-red-500">*</span>
        </label>
        <select v-model="formData.priority"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
          <option :value="AnnouncementPriority.NORMAL">普通</option>
          <option :value="AnnouncementPriority.HIGH">高优先级</option>
          <option :value="AnnouncementPriority.LOW">低优先级</option>
        </select>
      </div>

      <!-- 富文本编辑器 -->
      <div class="mb-6">
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          公告内容 <span class="text-red-500">*</span>
        </label>
        <QuillEditor v-model:content="formData.content" content-type="html" theme="snow" :toolbar="toolbarOptions"
          class="bg-white dark:bg-gray-700" :class="{ 'border-red-500': errors.content }" />
        <p v-if="errors.content" class="mt-1 text-sm text-red-500">{{ errors.content }}</p>
      </div>

      <!-- 弹窗设置 -->
      <div class="mb-6">
        <label class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          <input v-model="formData.isPopup" type="checkbox"
            class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" />
          <span>设置为弹窗公告</span>
        </label>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          勾选后,该公告将在用户首次访问时弹窗显示
        </p>
      </div>

      <!-- 有效期设置 -->
      <div class="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              生效时间
            </label>
            <input v-model="validFromInput" type="datetime-local"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              :class="{ 'border-red-500': errors.validFrom }" />
            <p v-if="errors.validFrom" class="mt-1 text-sm text-red-500">{{ errors.validFrom }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              失效时间
            </label>
            <input v-model="validUntilInput" type="datetime-local"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              :class="{ 'border-red-500': errors.validUntil }" />
            <p v-if="errors.validUntil" class="mt-1 text-sm text-red-500">{{ errors.validUntil }}</p>
          </div>
        </div>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
          提示: 留空表示永久有效。如果设置了有效期,只有在有效期内的公告才会显示
        </p>
      </div>

      <!-- 操作按钮 -->
      <div class="flex justify-end gap-3">
        <button type="button" @click="handleCancel"
          class="cancel-btn px-6">
          取消
        </button>
        <button type="submit" :disabled="isSubmitting"
          class="primary-btn px-6 disabled:opacity-50 disabled:cursor-not-allowed">
          {{ isSubmitting ? (mode === 'edit' ? '保存中...' : '创建中...') : (mode === 'edit' ? '保存' : '创建公告') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { AnnouncementStatus, AnnouncementPriority, type Announcement, type CreateAnnouncementRequest, type UpdateAnnouncementRequest } from '@/types/announcement'

/**
 * 组件属性
 */
interface Props {
  /** 表单模式 */
  mode?: 'create' | 'edit'
  /** 初始数据 (编辑模式) */
  initialData?: Announcement
}

/**
 * 组件事件
 */
interface Emits {
  /** 提交事件 */
  (e: 'submit', data: CreateAnnouncementRequest | UpdateAnnouncementRequest): void
  /** 取消事件 */
  (e: 'cancel'): void
}

const props = withDefaults(defineProps<Props>(), {
  mode: 'create',
})
const emit = defineEmits<Emits>()

// 富文本编辑器工具栏配置
const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  ['link'],
  ['clean'],
]

// 表单数据
const formData = reactive({
  title: '',
  content: '',
  status: AnnouncementStatus.DRAFT,
  priority: AnnouncementPriority.NORMAL,
  isPopup: true,
})

// 有效期输入 (datetime-local 格式)
const validFromInput = ref('')
const validUntilInput = ref('')

// 表单错误
const errors = reactive({
  title: '',
  content: '',
  validFrom: '',
  validUntil: '',
})

// 提交状态
const isSubmitting = ref(false)

/**
 * 初始化表单数据 (编辑模式)
 */
onMounted(() => {
  if (props.mode === 'edit' && props.initialData) {
    formData.title = props.initialData.title
    formData.content = props.initialData.content
    formData.status = props.initialData.status
    formData.priority = props.initialData.priority
    formData.isPopup = props.initialData.isPopup

    // 转换 UTC 时间为本地时间
    if (props.initialData.validFrom) {
      validFromInput.value = convertUTCToLocal(props.initialData.validFrom)
    }
    if (props.initialData.validUntil) {
      validUntilInput.value = convertUTCToLocal(props.initialData.validUntil)
    }
  }
})

/**
 * 将 UTC 时间转换为本地 datetime-local 格式
 */
const convertUTCToLocal = (utcTime: string): string => {
  const date = new Date(utcTime)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

/**
 * 将本地时间转换为 UTC ISO 字符串
 */
const convertLocalToUTC = (localTime: string): string => {
  return new Date(localTime).toISOString()
}

/**
 * 验证表单
 */
const validateForm = (): boolean => {
  // 清空错误
  errors.title = ''
  errors.content = ''
  errors.validFrom = ''
  errors.validUntil = ''

  let isValid = true

  // 验证标题
  if (!formData.title.trim()) {
    errors.title = '请输入公告标题'
    isValid = false
  } else if (formData.title.length > 200) {
    errors.title = '标题长度不能超过 200 字符'
    isValid = false
  }

  // 验证内容
  if (!formData.content.trim()) {
    errors.content = '请输入公告内容'
    isValid = false
  }

  // 验证有效期
  if (validFromInput.value && validUntilInput.value) {
    const validFrom = new Date(validFromInput.value)
    const validUntil = new Date(validUntilInput.value)
    if (validFrom >= validUntil) {
      errors.validUntil = '失效时间必须晚于生效时间'
      isValid = false
    }
  }

  return isValid
}

/**
 * 处理表单提交
 */
const handleSubmit = () => {
  if (!validateForm()) {
    return
  }

  isSubmitting.value = true

  try {
    const data: CreateAnnouncementRequest | UpdateAnnouncementRequest = {
      title: formData.title.trim(),
      content: formData.content,
      status: formData.status,
      priority: formData.priority,
      isPopup: formData.isPopup,
      validFrom: validFromInput.value ? convertLocalToUTC(validFromInput.value) : null,
      validUntil: validUntilInput.value ? convertLocalToUTC(validUntilInput.value) : null,
    }

    emit('submit', data)
  } finally {
    isSubmitting.value = false
  }
}

/**
 * 处理取消
 */
const handleCancel = () => {
  emit('cancel')
}
</script>
